import React from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts';
import MetricTile from '../MetricTile';
import PositionDirection from '../PositionDirection';
import PaginatedTable from '../PaginatedTable';
import CsvButton from '../CsvButton';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import { SERIES } from '../lib/colors';
import { fmtCount, fmtRatioPct, fmtPosition, fmtDate } from '../lib/format';

/**
 * Evidence for `search_headline` — the pinned panel, always first.
 *
 * `facts` shape consumed here (typed narrowly at this boundary, never as a global
 * union across panels — §5):
 *   { impressions, clicks, ctr, avg_position, position_direction,
 *     comparison_available, comparison_blocked_reason,
 *     comparison: { impressions_change_pct, clicks_change_pct, ctr_change_pct,
 *                   position_change_pct } | null,
 *     period, previous_period,
 *     trend: [{ date, clicks, impressions, ctr, position }] }
 *
 * ── Collapsed vs expanded (§7) ──────────────────────────────────────────────────
 * Collapsed: four figures and the 90-day trend. Expanded: the daily table, the comparison
 * ranges, the position distribution and CSV. The split is the spec's own row for this
 * panel, and it is what keeps the first screen readable for an owner who never expands
 * anything.
 *
 * Three things this component gets right that are easy to get wrong:
 *
 *  1. `comparison` arrives as `null` — not absent — when `comparison_available` is
 *     false (confirmed against the live Verde Ranch payload, 2026-08-13). Both cases
 *     mean "no comparison": no arrows, no `0%`, no `—` (§8). `comparison_blocked_reason`
 *     is already owner-readable prose, so it renders as the caption.
 *  2. The Position tile shows `avg_position` plus the VERBATIM `position_direction`, and
 *     its arrow and colour come from that string alone. `position_change_pct` is not
 *     rendered at all. See `lib/position.js` and `PositionDirection`.
 *  3. Vocabulary (§13): impressions are "showed up in searches" — never "views" or
 *     "reach"; clicks are "visits" — never "sessions", and never linked to the
 *     Analytics page, because these are Google's counts and will not match GA4.
 */

/**
 * CSV columns for the daily trend.
 *
 * Raw payload values, not the rendered strings — see `lib/csv.js` for why a spreadsheet
 * wants `0.0372` rather than `3.7%`.
 */
const TREND_CSV = [
  { header: 'Date', get: (r) => r.date },
  { header: 'Showed up in searches', get: (r) => r.impressions },
  { header: 'Visits', get: (r) => r.clicks },
  { header: 'Click rate', get: (r) => r.ctr },
  { header: 'Position', get: (r) => r.position },
];

const TREND_COLUMNS = [
  { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
  { key: 'impressions', label: 'Showed up in searches', align: 'right', render: (r) => fmtCount(r.impressions) },
  { key: 'clicks', label: 'Visits', align: 'right', render: (r) => fmtCount(r.clicks) },
  { key: 'ctr', label: 'Click rate', align: 'right', render: (r) => fmtRatioPct(r.ctr) },
  { key: 'position', label: 'Position', align: 'right', render: (r) => fmtPosition(r.position) },
];

const HeadlineStrip = ({ facts, sentence, collapsed = true }) => {
  // §8 is written against `comparison_available`, so that is what gates the arrows — not
  // merely whether a `comparison` object happens to be present. A payload carrying
  // `comparison_available: false` alongside a stale populated `comparison` would
  // otherwise render year-over-year arrows on a property the backend has explicitly
  // declared not comparable. Every fixture has the two fields agreeing, so nothing else
  // in the suite would catch that. Same defence `MoverBars` already applies to
  // `summary.comparable`.
  const comparison = facts?.comparison_available === true ? (facts.comparison || null) : null;
  const trend = Array.isArray(facts?.trend) ? facts.trend : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* The four accents are decorative — they separate the tiles and encode no
            judgement. See `MetricTile` for why the semantic scale is off limits here. */}
        <MetricTile
          label="Showed up in searches"
          value={fmtCount(facts?.impressions)}
          changePct={comparison?.impressions_change_pct}
          description="How many times your park appeared in Google results."
          variant="muted"
          icon="target"
        />
        <MetricTile
          label="Visits"
          value={fmtCount(facts?.clicks)}
          changePct={comparison?.clicks_change_pct}
          description="People who clicked a result and came to your site."
          variant="highlight"
          icon="click"
        />
        <MetricTile
          label="Click rate"
          value={fmtRatioPct(facts?.ctr)}
          changePct={comparison?.ctr_change_pct}
          description="Share of those appearances that turned into a visit."
          variant="success"
          icon="percent"
        />
        <MetricTile
          label="Position"
          value={fmtPosition(facts?.avg_position)}
          description="Your typical spot in the results, where lower is nearer the top."
          variant="subtle"
          icon="chart"
          // The direction string is itself a year-over-year claim — it ends "than a year
          // ago" — so §8's "no year-over-year indicator when `comparison_available` is
          // false" suppresses it along with the other three tiles' arrows. The backend
          // sends `position_direction: null` in that case; this gate is the defence
          // against a payload where the two fields disagree, which is the same defence
          // the arrows above already have.
          subLabel={
            facts?.comparison_available === true
              ? <PositionDirection direction={facts?.position_direction} />
              : undefined
          }
        />
      </div>

      {!facts?.comparison_available && facts?.comparison_blocked_reason && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {/* A colon, not a dash: §8 forbids a dash standing in for a missing change,
              and `HeadlineStrip.test.js` asserts no dash reaches this card at all. */}
          No year-over-year comparison yet: {facts.comparison_blocked_reason}.
        </p>
      )}

      {trend.length > 0 && (
        // The panel sentence is the chart's text alternative (§4): it is already
        // a complete plain-language description of this same data.
        <div className="min-h-[240px]" role="img" aria-label={sentence || 'Daily searches and visits'}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trend} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                tickFormatter={fmtDate}
                minTickGap={24}
                stroke="#94A3B8"
              />
              <YAxis yAxisId="impressions" tick={{ fontSize: 11 }} stroke={SERIES.impressions} width={52} />
              <YAxis yAxisId="clicks" orientation="right" tick={{ fontSize: 11 }} stroke={SERIES.clicks} width={44} />
              <ThemedChartTooltip labelFormatter={fmtDate} formatter={(v) => fmtCount(v)} />
              <Line
                yAxisId="impressions"
                type="monotone"
                dataKey="impressions"
                name="Showed up in searches"
                stroke={SERIES.impressions}
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={false}
              />
              <Line
                yAxisId="clicks"
                type="monotone"
                dataKey="clicks"
                name="Visits"
                stroke={SERIES.clicks}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
          {/* Dash vs solid, not hue alone — the chart has to survive greyscale and print. */}
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Dashed line: showed up in searches. Solid line: visits.
          </p>
        </div>
      )}

      {/* ── Expanded only: daily table, position bands, CSV (§7) ─────────────── */}
      {!collapsed && (
        <div className="space-y-4 border-t border-gray-200 dark:border-gray-800 pt-4">
          {facts?.period?.start_date && facts?.period?.end_date && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This period {fmtDate(facts.period.start_date)} – {fmtDate(facts.period.end_date)}
              {facts?.previous_period?.start_date && facts?.previous_period?.end_date
                ? `, compared with ${fmtDate(facts.previous_period.start_date)} – ${fmtDate(facts.previous_period.end_date)}`
                : ''}
              .
            </p>
          )}

          {trend.length > 0 && (
            <>
              <PaginatedTable
                columns={TREND_COLUMNS}
                rows={trend}
                rowKey={(r) => r.date}
                label="days"
                ariaLabel={sentence}
              />
              <CsvButton
                panelKey="search_headline"
                name="How your park is doing in search"
                columns={TREND_CSV}
                rows={trend}
                range={{ from: facts?.period?.start_date, to: facts?.period?.end_date }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Whether opening this card would change what is on screen (spec v2 §3).
 *
 * The expanded half is the daily table, the comparison periods, the position bands and
 * the CSV — and all four hang off the trend. A payload with no `trend` rows has a card
 * whose collapsed state is already everything it holds, and offering "Show more" there
 * promises depth that does not exist.
 *
 * @param {object} facts the panel's `facts`
 * @returns {boolean} true when expansion reveals something
 */
HeadlineStrip.hasDepth = (facts) => Array.isArray(facts?.trend) && facts.trend.length > 0;


export default HeadlineStrip;
